import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  ADD_FREE_PRODUCT_TO_BASKET,
  CREATE_BASKET_ITEM,
  DELETE_BASKET_ITEM,
  UPDATE_BASKET_ITEM,
  selectBasketsSelectedBasket,
} from '../../stores/order'
import { useOrderPickerOpener } from './use-order-picker'

export function useSelectedBasketItem(productId: string, { isFreeProductPromotion, isPurchasable }: { isFreeProductPromotion: boolean, isPurchasable: boolean }) {
  const dispatch = useDispatch()
  const selected = useSelector(selectBasketsSelectedBasket)
  const basket = selected && selected.basket
  const item = useMemo(() => {
    if (!basket) {
      return
    }
    const items = basket.items.filter((i) => i.productCode === productId)
    if (!items.length) {
      return
    }

    // there could be two items in the basket for the product, one free and one
    // not free so return the one which IS NOT FREE if there are two
    if (items.length > 1) {
      return items.find((i) => !i.isFreeDiscount)
    }

    // if the item is a free discount but it can also be purchase don't return
    const item = items[0]
    if (item.isFreeDiscount && isPurchasable) {
      return
    }
    return item
  }, [basket, isPurchasable, productId])

  const { onOpen } = useOrderPickerOpener()

  const onSelectBasket = useCallback(
    (splitQuantity: number, packQuantity: number) => {
      onOpen({
        type: 'ADD_PRODUCT',
        basketItemData: {
          productId,
          splitQuantity,
          packQuantity,
        },
      })
    },
    [onOpen, productId],
  )

  const onUpdate = useCallback(
    (packQuantity: number, splitQuantity: number) => {
      if (!basket) {
        return
      }

      if (isFreeProductPromotion && !isPurchasable && !item) {
        dispatch(
          ADD_FREE_PRODUCT_TO_BASKET.request({
            basketId: basket.id,
            productId,
          }),
        )
        return
      }

      if (!isPurchasable) {
        return
      }

      if (item) {
        if (splitQuantity < 1 && packQuantity < 1) {
          dispatch(
            DELETE_BASKET_ITEM.request({
              id: item.id,
              basketId: basket.id,
            }),
          )
        } else {
          dispatch(
            UPDATE_BASKET_ITEM.request({
              id: item.id,
              basketId: basket.id,
              packQuantity,
              splitQuantity,
            }),
          )
        }
      } else if (splitQuantity > 0 || packQuantity > 0) {
        dispatch(
          CREATE_BASKET_ITEM.request({
            basketId: basket.id,
            productId,
            packQuantity,
            splitQuantity,
          }),
        )
      }
    },
    [basket, isFreeProductPromotion, isPurchasable, item, dispatch, productId],
  )

  const onUpdateSplitQuantity = useCallback(
    (quantity: number) => {
      onUpdate(item ? item.packQuantity : 0, quantity)
    },
    [item, onUpdate],
  )

  const onUpdatePackQuantity = useCallback(
    (quantity: number) => {
      onUpdate(quantity, item ? item.splitQuantity : 0)
    },
    [item, onUpdate],
  )

  return {
    basket,
    item,
    onSelectBasket,
    onUpdatePackQuantity,
    onUpdateSplitQuantity,
  }
}
